import { expandBox } from './boxMargin';
import { patchCenterToOriginal } from './unletterboxBlob';
import type { UnletterboxContext } from './unletterboxBlob';

export type PointPrompt = { x: number; y: number; positive: boolean };

export type DerivePointPromptsArgs = {
  heatmap: Float32Array;
  gridH: number;
  gridW: number;
  threshold: number;
  // Axis-aligned box in the SAME space patchCenterToOriginal returns
  // (cropped-ROI pixels — see runLocate's coordinate-space notes).
  box: { x1: number; y1: number; x2: number; y2: number };
  ctx: UnletterboxContext;
  maxPositives?: number;
  maxNegatives?: number;
  // Grow the box outward by this fraction of its size when collecting *negative*
  // candidates (see expandBox). Positives are always taken from the original
  // box (they define the object), but a cast shadow is sub-threshold and leaks
  // just past the box edge — widening only the negative window puts a "stop
  // here" point on that ring. Defaults to 0 (legacy box-clipped behavior).
  marginFraction?: number;
};

const DEFAULT_MAX_POSITIVES = 3;
const DEFAULT_MAX_NEGATIVES = 3;
const MIN_POSITIVES_FOR_POINTS = 3;
const MIN_NEGATIVES_FOR_POINTS = 1;

// Negative-selection blend (see selectNegatives). Weights how much "close to
// the object" vs "low heatmap score" each count when ranking which
// sub-threshold patches become negative prompts. Equal weighting targets the
// boundary ring (cast shadows, holes) without carving under-activated object
// parts, which sit just below threshold and so lose on the score term.
const NEG_PROXIMITY_WEIGHT = 0.5;
const NEG_SCORE_WEIGHT = 0.5;

type ScoredPoint = { x: number; y: number; score: number };

// Pick up to `max` negative prompts. A cast shadow hugs the object and is
// sub-threshold, so the mask leaks into the low-score ring just outside the
// matched region. We rank sub-threshold patches by a blend of proximity to the
// nearest positive patch and (low) heatmap score, then take the lowest-cost
// ones — concentrating "stop here" signals exactly where SAM2 tends to bleed,
// rather than stride-scattering them to corners. Scores/distances are
// normalized within the window so the blend is scale-free.
function selectNegatives(
  positives: ScoredPoint[],
  negatives: ScoredPoint[],
  max: number,
): PointPrompt[] {
  const count = Math.min(max, negatives.length);
  if (count === 0) return [];

  // Distance from each negative to its nearest positive patch (squared root
  // for a real distance; ranking would be fine on squared, but normalization
  // reads more naturally in pixel space).
  const dists = negatives.map((n) => {
    let bestSq = Infinity;

    for (const p of positives) {
      const dx = n.x - p.x;
      const dy = n.y - p.y;
      const dSq = dx * dx + dy * dy;
      if (dSq < bestSq) bestSq = dSq;
    }

    return Math.sqrt(bestSq);
  });

  const dMin = Math.min(...dists);
  const dMax = Math.max(...dists);
  const sMin = Math.min(...negatives.map((n) => n.score));
  const sMax = Math.max(...negatives.map((n) => n.score));

  const norm = (v: number, lo: number, hi: number): number =>
    hi > lo ? (v - lo) / (hi - lo) : 0;

  // Lower cost = closer to the object AND lower score → likeliest shadow/hole.
  return negatives
    .map((n, i) => ({
      n,
      cost:
        NEG_PROXIMITY_WEIGHT * norm(dists[i], dMin, dMax) +
        NEG_SCORE_WEIGHT * norm(n.score, sMin, sMax),
    }))
    .sort((a, b) => a.cost - b.cost)
    .slice(0, count)
    .map(({ n }) => ({ x: n.x, y: n.y, positive: false }));
}

// Build SAM2 point prompts from a blob's heatmap window: positives on the
// strongest matched patches, negatives on the sub-threshold patches most
// likely to be shadow/holes the mask leaks into (low score, close to the
// object — see selectNegatives). Returns [] when the window is too sparse to
// sample reliably — the caller then sends a box-only prompt (no regression).
export function derivePointPrompts(
  args: DerivePointPromptsArgs,
): PointPrompt[] {
  const {
    heatmap,
    gridH,
    gridW,
    threshold,
    box,
    ctx,
    maxPositives = DEFAULT_MAX_POSITIVES,
    maxNegatives = DEFAULT_MAX_NEGATIVES,
    marginFraction = 0,
  } = args;

  const negBox = expandBox(box, marginFraction);

  const positives: ScoredPoint[] = [];
  const negatives: ScoredPoint[] = [];

  for (let row = 0; row < gridH; row += 1) {
    for (let col = 0; col < gridW; col += 1) {
      const { x, y } = patchCenterToOriginal(row, col, ctx);

      // Negatives may come from the expanded ring (shadow leaks past the box
      // edge); positives stay strictly in-box (they define the object). An
      // above-threshold patch outside the box is a neighbor, not this object —
      // it's neither a positive nor something to exclude, so skip it.
      const inNegBox =
        x >= negBox.x1 && x <= negBox.x2 && y >= negBox.y1 && y <= negBox.y2;

      if (!inNegBox) continue;

      const score = heatmap[row * gridW + col];

      if (score < threshold) {
        negatives.push({ x, y, score });
        continue;
      }

      const inBox = x >= box.x1 && x <= box.x2 && y >= box.y1 && y <= box.y2;
      if (inBox) positives.push({ x, y, score });
    }
  }

  // Fallback gate: attach points only when the window has enough matched
  // patches AND at least one region to exclude. Otherwise box-only.
  if (
    positives.length < MIN_POSITIVES_FOR_POINTS ||
    negatives.length < MIN_NEGATIVES_FOR_POINTS
  ) {
    return [];
  }

  const topPositives: PointPrompt[] = positives
    .sort((a, b) => b.score - a.score)
    .slice(0, maxPositives)
    .map((p) => ({ x: p.x, y: p.y, positive: true }));

  const negPicks = selectNegatives(positives, negatives, maxNegatives);

  return [...topPositives, ...negPicks];
}
